import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, addMonths, eachDay, isoWeekday, monthEnd, monthStart } from '@pz/core';
import { ErrorBox, IconButton, Loading } from './ui';
import { d, month as monthLabel } from '../lib/format';
import { useQ } from '../lib/hooks';
import { useMe } from '../lib/me';

/**
 * Days chosen one by one (request 24 of round 4): a month of the person's calendar where working days can be ticked.
 * Days without working hours (weekend, public holiday, day off by contract) and days another absence takes fully
 * cannot be chosen, so a choice never takes a day off twice; the server joins the days into ranges.
 * Round 5 (request 26): the calendar is the main way to choose; Shift+click chooses all working days between two days;
 * the holidays of the month are listed below (half holidays marked ½: a whole day of leave counts half there, no
 * separate half day needed). `overLeave`: Personal enters a sickness on days of leave (the leave days go back, § 9 BUrlG).
 */
export function DayPicker({ personId, value, onChange, overLeave }: { personId?: string; value: Set<string>; onChange: (next: Set<string>) => void; overLeave?: boolean }) {
  const { t, i18n } = useTranslation();
  const { me } = useMe();
  const [view, setView] = useState(() => monthStart([...value].sort()[0] ?? me.today));
  const from = view;
  const to = monthEnd(view);
  const q = useQ('leave.days', { from, to, personId });
  const byDate = new Map((q.data?.days ?? []).map((x) => [x.date, x]));
  const lead = isoWeekday(from) - 1;
  const cells: (string | null)[] = [...Array.from({ length: lead }, () => null), ...eachDay(from, to)];
  while (cells.length % 7) cells.push(null);
  const weekdays = Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }).format(new Date(`${addDays('2026-01-05', i)}T12:00:00Z`)));
  const [anchor, setAnchor] = useState<string | null>(null);
  const selectable = (date: string) => {
    const info = byDate.get(date);
    if (!info || info.workMinutes <= 0) return false;
    const taken = !!info.absence && (info.freeMinutes ?? 0) <= 0;
    return !taken || (!!overLeave && info.absence?.kind === 'vacation');
  };
  const toggle = (date: string, range: boolean) => {
    const next = new Set(value);
    if (range && anchor && anchor !== date) {
      // Shift+click: every working day between the last day clicked and this one (within the month shown)
      const [a, b] = anchor < date ? [anchor, date] : [date, anchor];
      for (const x of eachDay(a, b)) if (selectable(x)) next.add(x);
    } else if (next.has(date)) next.delete(date);
    else next.add(date);
    setAnchor(date);
    onChange(next);
  };
  const monthHolidays = (q.data?.days ?? []).filter((x) => x.holiday);
  return (
    <div className="rounded-md border border-line p-2">
      <div className="mb-2 flex items-center gap-2">
        <IconButton label={t('days.prev')} icon={<ChevronLeft className="size-4" />} onClick={() => setView(addMonths(view, -1))} />
        <span className="flex-1 text-center text-sm font-semibold">{monthLabel(view)}</span>
        <IconButton label={t('days.next')} icon={<ChevronRight className="size-4" />} onClick={() => setView(addMonths(view, 1))} />
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      <div role="grid" aria-label={monthLabel(view)} className="grid grid-cols-7 gap-1 text-center text-xs">
        {weekdays.map((w) => (
          <span key={w} role="columnheader" className="py-0.5 font-medium text-graphite">
            {w}
          </span>
        ))}
        {cells.map((date, i) => {
          if (!date) return <span key={`x${i}`} />;
          const info = byDate.get(date);
          const off = !info || info.workMinutes <= 0;
          const taken = !!info?.absence && (info.freeMinutes ?? 0) <= 0 && !(overLeave && info.absence.kind === 'vacation');
          const selected = value.has(date);
          const reason = info?.holiday ?? (taken && info?.absence ? t(`week.absence.${info.absence.kind as 'vacation'}`) : off ? t('days.noWorkday') : '');
          return (
            <button
              key={date}
              type="button"
              role="gridcell"
              aria-selected={selected}
              disabled={(off || taken) && !selected}
              title={[d(date), reason, info?.holidayPart === 'am' || info?.holidayPart === 'pm' ? t('days.halfHoliday') : ''].filter(Boolean).join(' · ')}
              onClick={(e) => toggle(date, e.shiftKey)}
              className={clsx(
                'num relative h-9 rounded text-sm outline-none focus-visible:ring-2 focus-visible:ring-accent',
                selected ? 'bg-accent font-semibold text-white' : off || taken ? 'cursor-not-allowed bg-sheet-2 text-faint' : 'border border-line hover:border-accent hover:text-accent',
                date === me.today && !selected && 'ring-1 ring-accent/50',
              )}
            >
              {Number(date.slice(8, 10))}
              {(info?.holidayPart === 'am' || info?.holidayPart === 'pm') && <span className="absolute top-0 right-0.5 text-[9px] leading-none font-semibold text-warn">½</span>}
              {(info?.holiday || (info?.absence && !selected)) && <span className={clsx('absolute inset-x-2 bottom-1 h-0.5 rounded', info?.holiday ? 'bg-warn' : 'bg-teal')} aria-hidden />}
            </button>
          );
        })}
      </div>
      {monthHolidays.length > 0 && (
        // the holidays of the month (calendar, company days and own holidays of the person)
        <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-graphite" aria-label={t('days.holidaysOfMonth')}>
          {monthHolidays.map((x) => (
            <li key={x.date}>
              <span className="num font-medium text-ink">{d(x.date).slice(0, 6)}</span> {x.holiday}
              {x.holidayPart === 'am' ? ` (${t('days.halfAm')})` : x.holidayPart === 'pm' ? ` (${t('days.halfPm')})` : ''}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-graphite">
        <span>{t('days.chosen', { count: value.size })}</span>
        {value.size > 0 && (
          <button type="button" className="underline-offset-2 hover:text-ink hover:underline" onClick={() => onChange(new Set())}>
            {t('days.clear')}
          </button>
        )}
        <span className="ml-auto flex items-center gap-1">
          <span className="inline-block h-0.5 w-3 rounded bg-warn" aria-hidden />
          {t('days.legendHoliday')}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-0.5 w-3 rounded bg-teal" aria-hidden />
          {t('days.legendAbsence')}
        </span>
      </div>
    </div>
  );
}
